import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { TrendingUp, EmojiEvents, BarChart } from "@mui/icons-material";
import LoadingSkeleton from "./LoadingSkeleton";
import { useNavigate } from "react-router-dom";

const PricesTab = ({
  country,
  currentCountryData,
  getChangeColor,
  getChangeIcon,
  currencySymbols,
  isLoggedIn,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [selectedPerformer, setSelectedPerformer] = useState(null);

  // console.log('Data:',currentCountryData.prices)

  if (!currentCountryData || currentCountryData.length === 0) {
    return <LoadingSkeleton />; // Or a spinner / placeholder component
  }

  // Helper to get currency symbol
  const getCurrencySymbol = (row) => {
    if (country === "Global") return currencySymbols?.["USD"] || "A$";
    return currencySymbols?.[row.currency] || row.currency || "A$";
  };

  // Fallbacks in case helpers are not passed (won't affect your design)
  const colorFor = getChangeColor
    ? getChangeColor
    : (c) =>
        parseFloat(String(c).replace("%", "")) >= 0
          ? theme.palette.success.main
          : theme.palette.error.main;
  const iconFor = getChangeIcon ? getChangeIcon : () => null;

  // Extract data safely
  const topPerformers = currentCountryData?.prices.topPerformers || [];
  const marketSummary = currentCountryData?.prices.marketSummary || {};

  const hasTopPerformers = topPerformers.length > 0;
  const hasMarketSummary = Object.keys(marketSummary).length > 0;

  // Fallback summary stats
  const averagePrice = marketSummary.averagePrice || "N/A";
  const totalMarketCap = marketSummary.totalMarketCap || "N/A";
  const priceRange = marketSummary.priceRange || "N/A";

  const handleLogin = () => {
    navigate("/login");
  };

  const handleRegister = () => {
    navigate("/register");
  };

  return (
    <Box sx={{ position: "relative" }}>
      <Box
        sx={{
          filter: !isLoggedIn ? "blur(4px)" : "none",
          pointerEvents: !isLoggedIn ? "none" : "auto",
        }}
      >
        {/* Header (responsive) */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            gap: 2,
            mb: 4,
            p: 3,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.primary.main,
              0.08
            )} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
          }}
        >
          <TrendingUp
            sx={{ fontSize: 32, color: theme.palette.primary.main }}
          />
          <Box>
            <Typography variant="h5" fontWeight="bold">
              Price Analysis
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {country} Market • Real-time pricing data
            </Typography>
          </Box>
        </Box>

        {/* Three cards in one row (responsive) */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr", // phones
              sm: "1fr 1fr", // tablets
              md: "1fr 1fr", // desktop and up
            },
            gap: { xs: 2, sm: 3, md: 4 },
            alignItems: "stretch",
            mb: 4,
          }}
        >
          {/* Top Performers */}
          <Card
            sx={{
              borderRadius: 4,
              boxShadow: theme.shadows[8],
              border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }}
          >
            <Box
              sx={{
                p: 3,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                background: `linear-gradient(135deg, ${alpha(
                  theme.palette.success.main,
                  0.1
                )} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)`,
                borderBottom: `1px solid ${alpha(
                  theme.palette.success.main,
                  0.2
                )}`,
              }}
            >
              <EmojiEvents sx={{ color: theme.palette.success.main }} />
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  Top Performers
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Materials with highest price gains
                </Typography>
              </Box>
            </Box>

            <Box sx={{ p: 3, flexGrow: 1 }}>
              {hasTopPerformers ? (
                topPerformers.map((item, i) => (
                  <Box
                    key={i}
                    onClick={() =>
                      setSelectedPerformer(
                        selectedPerformer?.metal === item.metal ? null : item
                      )
                    }
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      p: 2,
                      mb: i < topPerformers.length - 1 ? 2 : 0,
                      borderRadius: 2,
                      backgroundColor: alpha(theme.palette.success.main, 0.05),
                      border: `1px solid ${alpha(
                        theme.palette.success.main,
                        0.1
                      )}`,
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        backgroundColor: alpha(theme.palette.success.main, 0.1),
                        transform: "translateY(-3px)",
                      },
                      ...(selectedPerformer?.metal === item.metal && {
                        backgroundColor: alpha(
                          theme.palette.success.main,
                          0.15
                        ),
                        border: `2px solid ${theme.palette.success.main}`,
                      }),
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          backgroundColor: alpha(
                            theme.palette.success.main,
                            0.2
                          ),
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: "bold",
                          color: theme.palette.success.main,
                          flexShrink: 0,
                        }}
                      >
                        {i + 1}
                      </Box>
                      <Box>
                        <Typography variant="h6" fontWeight="bold">
                          {item.metal}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Market Cap: {item.market_cap}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ textAlign: "right" }}>
                      <Typography variant="h6" fontWeight="bold">
                        {/* {getCurrencySymbol(item)} */}
                        {item.price}
                      </Typography>
                      <Chip
                        size="small"
                        icon={iconFor(item.change_percent)}
                        label={item.change_percent}
                        sx={{
                          backgroundColor: alpha(
                            colorFor(item.change_percent),
                            0.2
                          ),
                          color: colorFor(item.change_percent),
                          fontWeight: "bold",
                          border: `1px solid ${alpha(
                            colorFor(item.change_percent),
                            0.3
                          )}`,
                        }}
                      />
                    </Box>
                  </Box>
                ))
              ) : (
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ p: 2, textAlign: "center" }}
                >
                  No data available for top performers.
                </Typography>
              )}
            </Box>
          </Card>

          {/* Market Summary */}
          <Card
            sx={{
              borderRadius: 4,
              boxShadow: theme.shadows[8],
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.primary.main,
                0.1
              )} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }}
          >
            {/* Header */}
            <Box
              sx={{
                p: 3,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                background: `linear-gradient(135deg, ${alpha(
                  theme.palette.primary.main,
                  0.1
                )} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
                borderBottom: `1px solid ${alpha(
                  theme.palette.primary.main,
                  0.2
                )}`,
              }}
            >
              <BarChart sx={{ color: theme.palette.primary.main }} />
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  {selectedPerformer
                    ? `${selectedPerformer.metal} Summary`
                    : "Market Summary"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedPerformer
                    ? "Performance overview for this material"
                    : "Key market statistics and metrics"}
                </Typography>
              </Box>
            </Box>

            {/* Content */}
            <CardContent sx={{ p: 0, flexGrow: 1 }}>
              {hasMarketSummary || selectedPerformer ? (
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  {selectedPerformer ? (
                    <>
                      {/* Price per kg */}
                      <Box
                        sx={{
                          textAlign: "center",
                          p: 3,
                          borderBottom: `1px solid ${alpha(
                            theme.palette.primary.main,
                            0.2
                          )}`,
                        }}
                      >
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          Price
                        </Typography>
                        <Typography
                          variant="h4"
                          fontWeight="bold"
                          color="primary.main"
                        >
                          {/* {getCurrencySymbol(selectedPerformer)} */}
                          {selectedPerformer.price}
                        </Typography>
                      </Box>

                      {/* Market Cap */}
                      <Box
                        sx={{
                          textAlign: "center",
                          p: 3,
                          borderBottom: `1px solid ${alpha(
                            theme.palette.primary.main,
                            0.2
                          )}`,
                        }}
                      >
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          Market Cap
                        </Typography>
                        <Typography
                          variant="h4"
                          fontWeight="bold"
                          color="secondary.main"
                        >
                          {selectedPerformer.market_cap}
                        </Typography>
                      </Box>

                      {/* Change (%) */}
                      <Box sx={{ textAlign: "center", p: 3 }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          Change (%)
                        </Typography>
                        <Chip
                          size="medium"
                          icon={iconFor(selectedPerformer.change_percent)}
                          label={selectedPerformer.change_percent}
                          sx={{
                            backgroundColor: alpha(
                              colorFor(selectedPerformer.change_percent),
                              0.2
                            ),
                            color: colorFor(selectedPerformer.change_percent),
                            fontWeight: "bold",
                            border: `1px solid ${alpha(
                              colorFor(selectedPerformer.change_percent),
                              0.3
                            )}`,
                          }}
                        />
                      </Box>
                    </>
                  ) : (
                    <>
                      {/* Average Price */}
                      <Box
                        sx={{
                          textAlign: "center",
                          p: 3,
                          borderBottom: `1px solid ${alpha(
                            theme.palette.primary.main,
                            0.2
                          )}`,
                        }}
                      >
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          Average Price
                        </Typography>
                        <Typography
                          variant="h4"
                          fontWeight="bold"
                          color="primary.main"
                        >
                          {/* {getCurrencySymbol({
                            currency: marketSummary.currency || "USD",
                          })} */}
                          {averagePrice}
                        </Typography>
                      </Box>

                      {/* Total Market Cap */}
                      <Box
                        sx={{
                          textAlign: "center",
                          p: 3,
                          borderBottom: `1px solid ${alpha(
                            theme.palette.primary.main,
                            0.2
                          )}`,
                        }}
                      >
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          Total Market Cap
                        </Typography>
                        <Typography
                          variant="h4"
                          fontWeight="bold"
                          color="secondary.main"
                        >
                          {/* {getCurrencySymbol({
                            currency: marketSummary.currency || "USD",
                          })} */}
                          {totalMarketCap}
                        </Typography>
                      </Box>

                      {/* Price Range */}
                      <Box sx={{ textAlign: "center", p: 3 }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          Price Range
                        </Typography>
                        <Typography variant="h5" fontWeight="bold">
                          {/* {getCurrencySymbol({
                            currency: marketSummary.currency || "USD",
                          })} */}
                          {priceRange}
                        </Typography>
                      </Box>
                    </>
                  )}
                </Box>
              ) : (
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ textAlign: "center", p: 3 }}
                >
                  No summary data available.
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Interactive Price Chart (kept as card, equal size) */}
          {/* <Card
            sx={{
              borderRadius: 4,
              boxShadow: theme.shadows[12],
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.secondary.main,
                0.05
              )} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              display: "flex",
              flexDirection: "column",
              textAlign: "center",
              p: 4,
              height: "100%",
            }}
          >
            <Box
              sx={{
                display: "inline-flex",
                p: 3,
                borderRadius: "50%",
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                mb: 3,
                alignSelf: "center",
              }}
            >
              <TrendingUp
                sx={{ fontSize: 48, color: theme.palette.primary.main }}
              />
            </Box>

            <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
              Interactive Price Charts
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 3, maxWidth: 420, mx: "auto" }}
            >
              Advanced charting and technical analysis tools are coming soon to
              help you make better trading decisions.
            </Typography>

            <Button
              variant="outlined"
              size="large"
              sx={{
                borderRadius: 3,
                textTransform: "none",
                fontWeight: "bold",
                mt: "auto",
                alignSelf: "center",
              }}
            >
              Get Notified When Available
            </Button>
          </Card> */}
        </Box>
      </Box>

      {/* Overlay CTA (if not logged in) */}
      {!isLoggedIn && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: 2,
            backgroundColor: alpha(theme.palette.background.default, 0.6),
            borderRadius: 3,
          }}
        >
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
            Please log in to access live market data
          </Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            {/* Log In Button */}
            <Button
              variant="contained"
              sx={{
                borderRadius: 2,
                px: 5,
                py: 1.5,
                backgroundColor: "#F4C542",
                color: "black",
                fontWeight: "bold",
                "&:hover": {
                  backgroundColor: "#E3AE27", // slightly darker hover shade
                },
              }}
              onClick={handleLogin}
            >
              Login
            </Button>

            {/* Sign Up Button */}
            <Button
              variant="contained"
              sx={{
                borderRadius: 2,
                px: 5,
                py: 1.5,
                backgroundColor: (theme) => theme.palette.common.black,
                color: (theme) => theme.palette.common.white,
                fontWeight: "bold",
                "&:hover": {
                  backgroundColor: (theme) => theme.palette.grey[900],
                },
              }}
              onClick={handleRegister}
            >
              Register
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default PricesTab;
